import { useMemo, useState } from 'react';
import { Division, DIVISION_STYLE, RegisteredEvent } from '@type/db-types';
import {
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import invariant from 'tiny-invariant';
import useEventContext from '@hooks/useEventContext';
import { updateRegistrantClass } from '@modules/supabase/events';
import { getDivisionLabel } from '@utils/event-utils';

export default function DivisionSelector({
    registrant,
    refresh,
}: {
    refresh: () => void;
    registrant: RegisteredEvent;
}) {
    const { classId, divisionId, membershipId } = registrant;
    const { event } = useEventContext();
    const [loading, setLoading] = useState(false);

    const options: Division[] = useMemo(() => event?.divisions || [], [event?.divisions]);

    const handleChange = async ({
        target: { value: newDivisionId },
    }: SelectChangeEvent<string>) => {
        const newDivision = options.find((d) => d.id === newDivisionId);
        invariant(membershipId, '[DivisionSelector] missing membership entry');
        invariant(newDivision, '[DivisionSelector] missing division');

        setLoading(true);

        const newClassId =
            newDivision.bracketStyle !== DIVISION_STYLE.MADISON_SYSTEM &&
            classId &&
            // Ensure the class is in the new division
            newDivision.classes.find(({ id }) => id === classId)
                ? classId
                : // clear out class if it's not in the new division
                  null;

        await updateRegistrantClass(newDivisionId, newClassId, membershipId);

        // invalidate local user data & refetch
        refresh();
        setLoading(false);
    };

    if (loading) return <CircularProgress />;

    return (
        <FormControl fullWidth>
            <Select
                id="permission-level-select"
                value={divisionId || ''}
                onChange={handleChange}
                // padding right to ensure the dropdown arrow does not overlap the text
                SelectDisplayProps={{ style: { padding: '0.5rem', paddingRight: '1.75rem' } }}
            >
                {options.map((division) => (
                    <MenuItem value={division.id} key={division.id}>
                        <Typography variant="body2">{getDivisionLabel(division)}</Typography>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
